import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { ProgressBarStep } from '.';

export type ProgressBarSingleTab = {
  type: 'single';
  index: string;
  isActive: boolean;
  isPassed: boolean;
  width: string;
  final: boolean;
};

export type ProgressBarMultiTab = {
  type: 'multi';
  index: string;
  width: string;
  subbars: (ProgressBarSingleTab | ProgressBarMultiTab)[];
  final: boolean;
};

export type ProgressBarTab = ProgressBarSingleTab | ProgressBarMultiTab;

export interface ProgressBarHook {
  tabs: ProgressBarTab[];
}

export const isPassed = (
  pathname: string,
  url: string | string[],
  allUrls: string[],
): boolean => {
  const pathIndex = allUrls
    .map((current) =>
      Boolean(
        matchPath(pathname, {
          path: current,
          exact: true,
          strict: false,
        }),
      ),
    )
    .indexOf(true);
  if (typeof url === 'string') {
    const urlIndex = allUrls
      .map((current) =>
        Boolean(
          matchPath(url, {
            path: current,
            exact: true,
            strict: false,
          }),
        ),
      )
      .indexOf(true);
    return pathIndex > urlIndex;
  }
  const urlIndexes = url.map(
    (currentURL) =>
      allUrls
        .map((current) =>
          Boolean(
            matchPath(currentURL, {
              path: current,
              exact: true,
              strict: false,
            }),
          ),
        )
        .indexOf(true) < pathIndex,
  );
  return urlIndexes.every((val) => val === true);
};

export const isActive = (
  path: string,
  itemPath: string | string[],
): boolean => {
  if (typeof itemPath === 'string')
    return Boolean(
      matchPath(path, {
        path: itemPath,
        exact: true,
        strict: false,
      }),
    );
  return itemPath.some((ip) =>
    Boolean(
      matchPath(path, {
        path: ip,
        exact: true,
        strict: false,
      }),
    ),
  );
};

const isSingleString = (url: string | string[]): url is string =>
  typeof url === 'string';

const extractUrls = (step: ProgressBarStep): string[] => {
  if (step.type === 'single') {
    const stepUrl = step.url;
    if (isSingleString(stepUrl)) return [stepUrl];
    return stepUrl;
  }
  return step.urls.map((substep) => extractUrls(substep)).flat();
};

const formatSteps = (
  steps: ProgressBarStep[],
  pathname: string,
  extractedUrls: string[],
  previousIndex?: string,
): ProgressBarTab[] => {
  const numSteps = steps.length;
  const width = `${100 / numSteps}%`;
  return steps.map((step: ProgressBarStep, index: number): ProgressBarTab => {
    if (step.type === 'single') {
      const active = isActive(pathname, step.url);
      const passed = isPassed(pathname, step.url, extractedUrls);
      const key = `${previousIndex ? `${previousIndex}-` : ''}${String(index)}`;
      return {
        type: 'single',
        isActive: active,
        isPassed: passed,
        index: key,
        width,
        final: index + 1 === steps.length,
      };
    }
    const key = `${previousIndex ? `${previousIndex}.` : ''}${String(index)}`;
    return {
      type: 'multi',
      width,
      index: key,
      subbars: formatSteps(step.urls, pathname, extractedUrls, key),
      final: index + 1 === steps.length,
    };
  });
};

export default function useProgressBar(
  steps: ProgressBarStep[],
): ProgressBarHook {
  const { pathname } = useLocation();

  const extractedUrls = useMemo(
    () => steps.map((step) => extractUrls(step)).flat(),
    [steps],
  );

  const tabs = useMemo(
    () => formatSteps(steps, pathname, extractedUrls),
    [steps, pathname, extractedUrls],
  );

  return {
    tabs,
  };
}
